import type { Booking, BookingGarment } from '../api/bookings';

/**
 * The pure half of the extension screen (spec Section 8 item 2.6).
 *
 * <p>
 * <strong>It quotes, and the server charges.</strong> Every figure here is what the counter says out
 * loud before the customer agrees — "two more days, sixteen hundred" — and the record keeps the
 * server's, frozen at the moment of granting. That is the same division `pricing.ts` draws for the
 * booking counter and the opposite of `invoices/register.ts`, which recomputes nothing: a clerk has
 * to watch this money move, and a filed document must never be re-derived.
 * </p>
 *
 * <p>
 * Every refusal below also exists on the server. Mirroring them is the standing rule — a screen must
 * not offer a write the server will reject — and here it is what stops a clerk promising days the
 * shop has already sold.
 * </p>
 */

/** Garments actually in the customer's hands. Only these are kept longer, and only these are charged. */
export function garmentsStillOut(booking: Booking | undefined): BookingGarment[] {
  return (booking?.wearers ?? []).flatMap((w) => w.garments).filter((g) => g.state === 'Issued');
}

/**
 * Whether this booking can be extended at all.
 *
 * An extension is mid-term: something has to be out. A booking whose garments have not gone out yet
 * and wants different dates is a date change — a different operation, with a different failure mode,
 * and one that is not built.
 */
export function canExtend(booking: Booking | undefined): boolean {
  return garmentsStillOut(booking).length > 0;
}

/** How many days a new end date adds. Inclusive at both ends, as every range in this product is. */
export function extraDays(possessionTo: string, toDate: string): number {
  if (!possessionTo || !toDate) {
    return 0;
  }

  const from = Date.parse(`${possessionTo.slice(0, 10)}T00:00:00Z`);
  const to = Date.parse(`${toDate.slice(0, 10)}T00:00:00Z`);

  if (Number.isNaN(from) || Number.isNaN(to)) {
    return 0;
  }

  return Math.round((to - from) / 86_400_000);
}

/**
 * What the extra days will cost, per garment and in total.
 *
 * <p>
 * <strong>A garment with no rate anywhere is reported rather than priced at zero</strong>, because
 * the server refuses exactly that: a shop that has not priced an extra day has not decided extra
 * days are free. The screen names those garments instead of quietly quoting a lower figure.
 * </p>
 */
export function quoteExtension(
  garments: BookingGarment[],
  days: number,
  fallbackPerDay: number | null,
): { total: number; unpriced: BookingGarment[] } {
  const unpriced: BookingGarment[] = [];

  let total = 0;

  for (const garment of garments) {
    const perDay = garment.extensionRatePerDay ?? fallbackPerDay ?? 0;

    if (perDay <= 0) {
      unpriced.push(garment);
      continue;
    }

    total += perDay * Math.max(0, days);
  }

  return { total, unpriced };
}

/**
 * The one sentence standing between this screen and Save, or null when nothing is.
 *
 * <p>
 * One at a time, most-blocking first — the invoicing readiness notice's rule. A list of four
 * complaints under a form reads as a wall, and only the first is actionable anyway.
 * </p>
 */
export function extensionProblem(input: {
  booking: Booking | undefined;
  toDate: string;
  days: number;
  unpriced: BookingGarment[];
  maxDays: number;
}): string | null {
  const { booking, toDate, days, unpriced, maxDays } = input;

  if (!booking || !canExtend(booking)) {
    return 'Nothing is out on this booking, so there is nothing to extend.';
  }

  if (!toDate) {
    return 'Say which day the garments now come back.';
  }

  if (days <= 0) {
    return 'An extension has to end later than the booking already does.';
  }

  if (days > maxDays) {
    return `An extension cannot run more than ${maxDays} days past the booking.`;
  }

  if (unpriced.length > 0) {
    const names = unpriced.map((g) => g.serialCode).join(', ');

    return `Nothing prices an extra day of ${names}. Set a per-day rate on its card, or a shop-wide late-fee rate.`;
  }

  return null;
}

/**
 * How far past the booking's end the counter may go — the server's own ceiling, mirrored.
 *
 * A constant on both sides rather than a setting: it is a guard against a typo taking a garment off
 * the rail for a decade, not a policy a shop tunes.
 */
export const maxExtensionDays = 60;

/** The phone's one-tap answers — "two more days" is how the conversation goes. */
export const quickExtensions: readonly number[] = [1, 2, 3];

/** The new return day for a number of extra days, inclusive ranges as everywhere. */
export function extendedTo(possessionTo: string, days: number): string {
  const at = new Date(`${possessionTo.slice(0, 10)}T00:00:00Z`);

  if (Number.isNaN(at.getTime())) {
    return possessionTo;
  }

  at.setUTCDate(at.getUTCDate() + days);

  return at.toISOString().slice(0, 10);
}
